// Upload-from-phone sessions for VITE_CREDENTIALING_MOCKS. They live in
// localStorage so the phone page, opened in another tab of the same browser,
// shares them the way a real phone shares them through the server. Only file
// metadata is kept: names, sizes and types, never record data.
import {
  CreateUploadSessionRequest,
  PhoneUploadFile,
  PhoneUploadInfo,
  UploadSession,
  UploadSessionKind,
  UploadSessionStatus,
} from '@src/types/personnel';
import { mockApiError } from '../credentialingContract';

const STORAGE_KEY = 'auxilium-mock-upload-sessions';
const LIFETIME_MS = 15 * 60 * 1000;
const MAX_ATTEMPTS = 5;

type MockSession = {
  id: string;
  token: string;
  code: string;
  expiresAt: string;
  kind: UploadSessionKind;
  maxFiles: number;
  slots: Array<string>;
  attempts: number;
  verified: boolean;
  status: UploadSessionStatus['status'];
  files: Array<PhoneUploadFile>;
};

const read = (): Record<string, MockSession> => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

const write = (sessions: Record<string, MockSession>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // a private window without storage keeps the computer side working
  }
};

const random = (length: number) =>
  Array.from({ length }, () => Math.floor(Math.random() * 10)).join('');

const withExpiry = (session: MockSession): MockSession =>
  session.status === 'waiting' && Date.parse(session.expiresAt) < Date.now()
    ? { ...session, status: 'expired' }
    : session;

const save = (session: MockSession) => {
  write({ ...read(), [session.id]: session });
  return session;
};

const byId = (id: string) => {
  const session = read()[id];
  if (!session) throw mockApiError(404, 'Upload link not found');
  return withExpiry(session);
};

const byToken = (token: string) => {
  const session = Object.values(read()).find((item) => item.token === token);
  if (!session) throw mockApiError(410, 'This link has ended');
  return withExpiry(session);
};

const ensureOpen = (session: MockSession) => {
  if (session.status !== 'waiting') {
    throw mockApiError(410, 'This link has ended');
  }
};

export const uploadSessionMocks = {
  create: ({
    kind,
    maxFiles,
    slots,
  }: CreateUploadSessionRequest): UploadSession => {
    const id = `session-${Date.now()}`;
    const token = `${random(6)}${Date.now().toString(36)}`;
    const session = save({
      attempts: 0,
      code: random(6),
      expiresAt: new Date(Date.now() + LIFETIME_MS).toISOString(),
      files: [],
      id,
      kind,
      maxFiles,
      slots,
      status: 'waiting',
      token,
      verified: false,
    });

    return {
      code: session.code,
      expiresAt: session.expiresAt,
      id,
      url: `${window.location.origin}/upload/${token}`,
    };
  },

  // the mock cannot text anyone; it only checks the link is still open
  sendLink: ({ id }: { id: string; mobileNumber: string }) => {
    ensureOpen(byId(id));
    return { sent: true };
  },

  status: ({ id }: { id: string }): UploadSessionStatus => {
    const { expiresAt, files, status } = byId(id);
    return { expiresAt, files, status };
  },

  end: ({ id }: { id: string }) => {
    const session = read()[id];
    if (session) save({ ...session, status: 'ended' });
    return { ended: true };
  },

  // the phone page's countdown, before the code: no data, only the state
  publicStatus: ({ token }: { token: string }) => {
    const { expiresAt, status } = byToken(token);
    return { expiresAt, status };
  },

  // five wrong codes end the link (US-D2.32)
  verify: ({
    code,
    token,
  }: {
    token: string;
    code: string;
  }): PhoneUploadInfo => {
    const session = byToken(token);
    ensureOpen(session);

    if (session.code !== code) {
      const attempts = session.attempts + 1;
      const ended = attempts >= MAX_ATTEMPTS;
      save({ ...session, attempts, status: ended ? 'ended' : session.status });
      throw ended
        ? mockApiError(410, 'This link has ended')
        : mockApiError(
            401,
            `That code is not right. ${MAX_ATTEMPTS - attempts} tries left.`
          );
    }

    save({ ...session, verified: true });
    return {
      contractorName: 'ABC Transportation',
      expiresAt: session.expiresAt,
      kind: session.kind,
      maxFiles: session.maxFiles,
      slots: session.slots,
    };
  },

  upload: ({ body, token }: { token: string; body: FormData }) => {
    const session = byToken(token);
    ensureOpen(session);
    if (!session.verified) throw mockApiError(401, 'Enter the code first');

    const meta = JSON.parse(String(body.get('meta') ?? '{}')) as {
      slots?: Array<string | null>;
    };
    const files = body
      .getAll('files')
      .filter((item): item is File => item instanceof File);
    if (!files.length) throw mockApiError(400, 'Add a file first');
    if (files.length > session.maxFiles) {
      throw mockApiError(400, `Up to ${session.maxFiles} files`);
    }

    const uploaded = files.map((file, index) => ({
      id: `phone-${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      slot: meta.slots?.[index] ?? undefined,
      type: file.type,
    }));

    // once used, the link is done
    save({ ...session, files: uploaded, status: 'used' });
    return { uploaded: uploaded.length };
  },
};
